import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { getResolution } from 'web/src/utilities/resolution';
import { RecipeHeader } from '../../api/models/recipe-header.models';
import { DeleteRecipeMenuDetailRequest } from '../../api/models/recipe-menu-detail.models';
import { UpdateRecipeMenuHeaderPrivateUsersRequest } from '../../api/models/recipe-menu-header-private-users';
import {
  CopyRecipeMenuHeaderRequest,
  CreateRecipeMenuHeaderRequest,
  DeleteRecipeMenuHeaderRequest,
  RecipeMenuHeader,
  UpdateRecipeMenuHeaderRequest,
} from '../../api/models/recipe-menu-header.models';
import {
  CreateRecipeMenuWorksheetRequest,
  DeleteRecipeMenuWorksheetRequest,
  RecipeMenuWorksheetHeader,
  UpdateRecipeMenuWorksheetRecipeFoodCostAndSellPriceRequest,
  UpdateRecipeMenuWorksheetRecipeTotalNumberSoldRequest,
  UpdateRecipeMenuWorksheetRequest,
} from '../../api/models/recipe-menu-worksheet.models';
import RecipeMenuDetailService from '../../api/services/recipe-menu-detail.service';
import RecipeMenuDiaryService from '../../api/services/recipe-menu-diary.service';
import RecipeMenuHeaderPrivateUserService from '../../api/services/recipe-menu-header-private-user.service';
import RecipeMenuHeaderService from '../../api/services/recipe-menu-header.service';
import RecipeMenuWorksheetService from '../../api/services/recipe-menu-worksheet.service';
import { addDays, truncateTime } from '../../helpers';
import { useAppInsightsLogger } from '../../logging';
import { Resolution, UserActivityAction, UserActivityActionSummary, UserActivityPageName } from '../../models';
import { globalSlice, logUserActivity } from '../common';
import { AppDispatch, AppThunk, RootState } from '../store';
import { menusSlice } from './menus.slice';
import { recipesSlice, selectAllRecipeHeaders } from './recipes.slice';
import { getRecipeHeaders } from './recipes.thunks';

const appInsightsLogger = useAppInsightsLogger();
const recipeMenuHeaderService = RecipeMenuHeaderService.getInstance();
const recipeMenuDetailService = RecipeMenuDetailService.getInstance();
const recipeMenuHeaderPrivateUserService = RecipeMenuHeaderPrivateUserService.getInstance();
const recipeMenuDiaryService = RecipeMenuDiaryService.getInstance();
const recipeMenuWorksheetService = RecipeMenuWorksheetService.getInstance();

export const getMenu =
  (customerId: string, recipeMenuHeaderId: string): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!getState().menus.isMenuHeadersLoading) dispatch(menusSlice.actions.setIsLoadingMenuHeaders(true));
    try {
      const { data } = await recipeMenuHeaderService.getRecipeMenuHeader(customerId, recipeMenuHeaderId);
      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setMenuHeader(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
    dispatch(menusSlice.actions.setIsLoadingMenuHeaders(false));
  };

export const getMenus =
  (customerId: string, onSuccess?: (data: RecipeMenuHeader[]) => void, isPreload?: boolean): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (getState().menus.gridParameters?.customerId === customerId) {
      return; // no-op
    }

    if (!isPreload) {
      dispatch(menusSlice.actions.setGridParameters({ customerId }));
    }

    if (!getState().menus.isMenuHeadersLoading) dispatch(menusSlice.actions.setIsLoadingMenuHeaders(true));
    try {
      const { data } = await recipeMenuHeaderService.getRecipeMenuHeaders(customerId);
      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setMenuHeaders(data.ResultObject));
        onSuccess?.(data.ResultObject);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
    dispatch(menusSlice.actions.setIsLoadingMenuHeaders(false));
  };

export const createMenuHeader =
  (request: CreateRecipeMenuHeaderRequest, resolution: Resolution): AppThunk<Promise<RecipeMenuHeader | undefined>> =>
  async (dispatch: AppDispatch) => {
    let result;
    try {
      const { data } = await recipeMenuHeaderService.createRecipeMenuHeader(request);
      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setMenuHeader(data.ResultObject));
        dispatch(menusSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CreateMenu,
            pageName: UserActivityPageName.Menus,
            actionSummary: UserActivityActionSummary.CreateMenu,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
    return result;
  };

export const updateMenuHeader =
  (request: UpdateRecipeMenuHeaderRequest, resolution: Resolution): AppThunk<Promise<RecipeMenuHeader | undefined>> =>
  async (dispatch: AppDispatch) => {
    let result;
    try {
      const { data } = await recipeMenuHeaderService.updateRecipeMenuHeader(request);
      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setMenuHeader(data.ResultObject));
        dispatch(menusSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.EditMenu,
            pageName: UserActivityPageName.Menus,
            actionSummary: UserActivityActionSummary.UpdateMenu,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
    return result;
  };

export const copyMenuHeader =
  (request: CopyRecipeMenuHeaderRequest, resolution: Resolution): AppThunk<Promise<RecipeMenuHeader | undefined>> =>
  async (dispatch: AppDispatch) => {
    let result;
    try {
      const { data } = await recipeMenuHeaderService.copyRecipeMenuHeader(request);
      if (data.IsSuccess) {
        dispatch(menusSlice.actions.resetGridParameters());
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.CopyMenu,
            pageName: UserActivityPageName.Menus,
            actionSummary: UserActivityActionSummary.CopyMenu,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
    return result;
  };

export const deleteMenuHeader =
  (request: DeleteRecipeMenuHeaderRequest, resolution: Resolution): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    let result = false;
    try {
      const { data } = await recipeMenuHeaderService.deleteRecipeMenuHeader(request);
      if (data.IsSuccess) {
        result = true;
        dispatch(menusSlice.actions.removeMenuHeader(request.RecipeMenuHeaderId));
        dispatch(menusSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.DeleteMenu,
            pageName: UserActivityPageName.Menus,
            actionSummary: UserActivityActionSummary.DeleteMenu,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

export const updateMenuDetails =
  (customerId: string, menuHeaderId: string): AppThunk<Promise<boolean>> =>
  async (dispatch, getState) => {
    let result = false;
    try {
      if (!getState().recipes.isRecipeHeadersLoading) dispatch(recipesSlice.actions.setIsLoadingRecipeHeaders(true));

      const recipeUpdates = selectAllRecipeHeaders(getState().recipes.recipeHeaders).map((m) => ({
        IsAdd: m.IsOnMenu,
        RecipeHeaderId: m.RecipeHeaderId,
        RecipeName: m.RecipeName,
      }));

      const { data } = await recipeMenuDetailService.updateRecipeMenuDetails({
        CustomerId: customerId,
        RecipeMenuHeaderId: menuHeaderId,
        RecipeUpdates: recipeUpdates,
      });
      if (data.IsSuccess) {
        result = true;
        dispatch(menusSlice.actions.resetGridParameters());
        dispatch(recipesSlice.actions.resetGridParameters());
        dispatch(getRecipeHeaders(customerId, menuHeaderId));
        dispatch(
          logUserActivity({
            action: UserActivityAction.UpdateMenuDetails,
            pageName: UserActivityPageName.MenuDetails,
            actionSummary: UserActivityActionSummary.UpdateMenuDetails,
            resolution: getResolution(),
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
        dispatch(recipesSlice.actions.resetRecipes());
        dispatch(getRecipeHeaders(customerId, menuHeaderId));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
    return result;
  };

export const deleteMenuDetail =
  (recipeHeader: RecipeHeader, request: DeleteRecipeMenuDetailRequest): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    let result = false;
    try {
      const { data } = await recipeMenuDetailService.deleteRecipeMenuDetail(request);
      if (data.IsSuccess) {
        result = true;
        dispatch(
          recipesSlice.actions.setRecipeHeader({ ...recipeHeader, IsOnMenu: false, RecipeMenuDetailId: undefined })
        );
        dispatch(menusSlice.actions.resetGridParameters());
        dispatch(
          logUserActivity({
            action: UserActivityAction.DeleteMenuDetail,
            pageName: UserActivityPageName.MenuDetails,
            actionSummary: UserActivityActionSummary.DeleteMenuDetail,
            resolution: getResolution(),
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
      });
    }
    return result;
  };

// Recipe Menu header private users

export const getRecipeMenuHeaderPrivateUsers =
  (recipeMenuHeaderId: string, viewAsCustomerId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().menus.isRecipeMenuHeaderPrivateUsersLoading) {
        dispatch(menusSlice.actions.setIsRecipeMenuHeaderPrivateUsersLoading(true));
      }

      const { data } = await recipeMenuHeaderPrivateUserService.getRecipeMenuHeaderPrivateUsers(
        recipeMenuHeaderId,
        viewAsCustomerId
      );

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setRecipeMenuHeaderPrivateUsers(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(menusSlice.actions.setIsRecipeMenuHeaderPrivateUsersLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const updateRecipeMenuHeaderPrivateUsers =
  (request: UpdateRecipeMenuHeaderPrivateUsersRequest, resolution: Resolution, onSuccess?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await recipeMenuHeaderPrivateUserService.updateRecipeMenuHeaderPrivateUsers(request);

      if (data.IsSuccess) {
        dispatch(
          logUserActivity({
            action: UserActivityAction.ManagePrivateMenuUsers,
            pageName: UserActivityPageName.PrivateMenuUsers,
            actionSummary: UserActivityActionSummary.ManagePrivateMenuUsers,
            resolution: resolution,
          })
        );
        onSuccess?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

// Recipe menu worksheets

export const getMenuWorksheets =
  (customerId: string): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().menus.isLoadingWorkSheetList) {
        dispatch(menusSlice.actions.setLoadingWorkSheetList(true));
      }

      const { data } = await recipeMenuWorksheetService.getRecipeMenuWorksheetHeaders(customerId);

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setMenuWorkSheetList(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(menusSlice.actions.setLoadingWorkSheetList(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const createMenuWorkSheet =
  (request: CreateRecipeMenuWorksheetRequest): AppThunk<Promise<RecipeMenuWorksheetHeader | undefined>> =>
  async (dispatch: AppDispatch): Promise<RecipeMenuWorksheetHeader | undefined> => {
    let result;

    try {
      const { data } = await recipeMenuWorksheetService.createRecipeMenuWorksheetHeader(request);

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.createMenuWorkSheet(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      result = data?.ResultObject;
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }

    return result;
  };

export const updateMenuWorkSheet =
  (request: UpdateRecipeMenuWorksheetRequest, onSuccess?: (worksheetHeaderId: string) => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      const { data } = await recipeMenuWorksheetService.updateRecipeMenuWorksheetHeader(request);

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.updateMenuWorkSheet(data.ResultObject));
        onSuccess?.(data.ResultObject.RecipeMenuWorksheetHeaderId);
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const deleteMenuWorkSheet =
  (request: DeleteRecipeMenuWorksheetRequest, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      const { data } = await recipeMenuWorksheetService.deleteRecipeMenuWorksheetHeader(request);

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.deleteMenuWorkSheet(request.RecipeMenuWorksheetHeaderId));
        onSuccess?.();
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const getMenuWorkSheetDetails =
  (worksheetHeaderId: string): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!getState().menus.isLoadingWorkSheetDetails) {
        dispatch(menusSlice.actions.setLoadingMenuWorkSheetDetails(true));
      }

      const { data } = await recipeMenuWorksheetService.getRecipeMenuWorksheet(worksheetHeaderId);

      if (data.IsSuccess) {
        const { RecipeMenuWorksheetDetails, ...header } = data.ResultObject;
        dispatch(menusSlice.actions.updateMenuWorkSheet(header));
        dispatch(menusSlice.actions.setMenuWorkSheetDetails(RecipeMenuWorksheetDetails));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(menusSlice.actions.setLoadingMenuWorkSheetDetails(false));
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const updateMenuWorkSheetTotalSold =
  (request: UpdateRecipeMenuWorksheetRecipeTotalNumberSoldRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      const { data } = await recipeMenuWorksheetService.updateTotalSold(request);

      if (data.IsSuccess) {
        const { RecipeMenuWorksheetDetails, ...header } = data.ResultObject;
        dispatch(menusSlice.actions.updateMenuWorkSheet(header));
        dispatch(menusSlice.actions.setMenuWorkSheetDetails(RecipeMenuWorksheetDetails));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

export const updateMenuWorkSheetFoodCostAndSellPrice =
  (request: UpdateRecipeMenuWorksheetRecipeFoodCostAndSellPriceRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      const { data } = await recipeMenuWorksheetService.updateFoodCostAndSellPrice(request);

      if (data.IsSuccess) {
        const { RecipeMenuWorksheetDetails, ...header } = data.ResultObject;
        dispatch(menusSlice.actions.updateMenuWorkSheet(header));

        if (RecipeMenuWorksheetDetails.length > 0) {
          dispatch(menusSlice.actions.updateMenuWorkSheetDetail(RecipeMenuWorksheetDetails[0]));
        }
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (error) {
      appInsightsLogger.trackException({
        exception: error,
        severityLevel: SeverityLevel.Error,
      });
    }
  };

// Menu diaries
export const getMenuDiaries =
  (
    customerId: string,
    resolution: Resolution,
    recipeMenuHeaderId?: string,
    startDate?: Date | string,
    endDate?: Date | string
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      if (!getState().menus.isMenuDiariesLoading) {
        dispatch(menusSlice.actions.setIsMenuDiariesLoading(true));
      }

      if (!endDate) {
        endDate = truncateTime(new Date());
        endDate = addDays(endDate, 1);
      }
      if (!startDate) {
        startDate = truncateTime(new Date());
        startDate = truncateTime(addDays(startDate, -7));
      }

      const { data } = await recipeMenuDiaryService.getRecipeMenuDiarys({
        CustomerId: customerId,
        RecipeMenuHeaderId: recipeMenuHeaderId,
        StartDate: startDate,
        EndDate: endDate,
      });

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.setMenuDiaries(data.ResultObject));
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }

      dispatch(menusSlice.actions.setIsMenuDiariesLoading(false));
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };

export const revertMenuDiaryAction =
  (recipeMenuDiaryId: string, resolution: Resolution, onSuccess?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await recipeMenuDiaryService.revertRecipeMenuDiaryAction({
        RecipeMenuDiaryId: recipeMenuDiaryId,
      });

      if (data.IsSuccess) {
        dispatch(menusSlice.actions.removeMenuDiary(recipeMenuDiaryId));
        onSuccess?.();
        dispatch(
          logUserActivity({
            action: UserActivityAction.RevertDeletedMenu,
            pageName: UserActivityPageName.Menus,
            actionSummary: UserActivityActionSummary.RevertDeletedMenu,
            resolution: resolution,
          })
        );
      } else {
        dispatch(globalSlice.actions.setErrorDialogContent({ messages: data.ErrorMessages }));
      }
    } catch (exception) {
      appInsightsLogger.trackException({ exception });
    }
  };
